/**
 * ElevenLabs Audio Service — frontend client for the FIRST audio-output nodes.
 *
 * Talks to our backend proxy at /api/elevenlabs/* (which holds the platform
 * ELEVENLABS_API_KEY, reserves/confirms credits, and uploads the result to R2).
 * All three endpoints are SYNCHRONOUS — one POST returns the permanent audio URL,
 * so there's no polling like FalAI/PiAPI video.
 */

import { getApiBase } from '../config/api';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

export interface ElevenLabsAudioResponse {
  content: string;
  type: 'audio';
  context: null;
  audioUrl: string;
}

class ElevenLabsAudioService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBase();
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) headers['x-user-id'] = user.id;
    } catch {
      /* anonymous — server will reject with 401 */
    }
    return headers;
  }

  private async post(path: string, body: Record<string, unknown>): Promise<ElevenLabsAudioResponse> {
    const headers = await this.authHeaders();
    const response = await fetch(`${this.baseUrl}/api/elevenlabs/${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error((errorData as any).message || (errorData as any).error || `Audio generation failed: ${response.status}`) as any;
      error.suggestions = ['Try again', 'Shorten the text', 'Check your credit balance'];
      throw error;
    }

    const result = await response.json();
    if (!result.audioUrl) {
      throw new Error('No audio URL returned from ElevenLabs');
    }
    logger.debug('ElevenLabs audio generated:', { url: String(result.audioUrl).substring(0, 80) });
    return { content: result.audioUrl, type: 'audio', context: null, audioUrl: result.audioUrl };
  }

  /** Text-to-speech. `model` defaults to eleven_v3 server-side. */
  async generateSpeech(params: {
    text: string;
    voiceId?: string;
    model?: string;
    format?: string;
    stability?: number;
    similarityBoost?: number;
  }): Promise<ElevenLabsAudioResponse> {
    return this.post('tts', params as Record<string, unknown>);
  }

  /** Sound effect from a text description. */
  async generateSoundEffect(params: {
    text: string;
    durationSeconds?: number;
    promptInfluence?: number;
    format?: string;
  }): Promise<ElevenLabsAudioResponse> {
    return this.post('sound-effects', params as Record<string, unknown>);
  }

  /** Music from a text prompt. `model` defaults to music_v1 server-side. */
  async generateMusic(params: {
    prompt: string;
    musicLengthMs?: number;
    model?: string;
    format?: string;
  }): Promise<ElevenLabsAudioResponse> {
    return this.post('music', params as Record<string, unknown>);
  }
}

export const elevenLabsAudioService = new ElevenLabsAudioService();
export default elevenLabsAudioService;
